import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
import { useAuthContext } from "src/auth/hooks";
import SvgColor from 'src/components/svg-color';
import { sub } from 'date-fns';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { user } = useAuthContext();
  const menu = user && user.menu?user.menu:[];

  // Determine subheader order by the minimum priority among each subheader's top-level items
  const subheaderMinPriority = {};
  menu.forEach(item => {
    if (item.parent_id !== 0) return;
    const sh = item.subheader;
    if (subheaderMinPriority[sh] === undefined || item.priority < subheaderMinPriority[sh]) {
      subheaderMinPriority[sh] = item.priority;
    }
  });

  // Build a map of parent id → priority so children can be grouped under their parent
  const parentPriorityMap = {};
  menu.forEach(item => {
    if (item.parent_id === 0) parentPriorityMap[item.id] = item.priority;
  });

  // Sort: 1) by subheader order, 2) by parent priority, 3) parent before its children, 4) child priority
  menu.sort((a, b) => {
    const aShP = subheaderMinPriority[a.subheader] ?? Infinity;
    const bShP = subheaderMinPriority[b.subheader] ?? Infinity;
    if (aShP !== bShP) return aShP - bShP;

    const aParentP = a.parent_id === 0 ? a.priority : (parentPriorityMap[a.parent_id] ?? Infinity);
    const bParentP = b.parent_id === 0 ? b.priority : (parentPriorityMap[b.parent_id] ?? Infinity);
    if (aParentP !== bParentP) return aParentP - bParentP;

    // Same parent bucket: top-level item comes before its children
    if (a.parent_id === 0 && b.parent_id !== 0) return -1;
    if (a.parent_id !== 0 && b.parent_id === 0) return 1;

    // Both children of the same parent: sort by their own priority, then id
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.id - b.id;
  });

  // Deduplicate by menu id for display — multiple permissions can share the same menu item.
  const seenMenuIds = new Set();
  const displayMenu = menu.filter(item => {
    if (seenMenuIds.has(item.id)) return false;
    seenMenuIds.add(item.id);
    return true;
  });

  const subheader = [];
  for(var m=0;m<displayMenu.length;m++){
    let _sh = subheader.filter(s=>s.subheader == t(displayMenu[m]['subheader']));
    const path_arr = displayMenu[m]['path'].trim().split('.');
    let _path = paths;
      for(var p=1;p<path_arr.length;p++){
        if(_path[path_arr[p]])
        _path = _path[path_arr[p]];
        else{
          _path='/404';
          break;
        }
      }
    if(_sh.length>0){
      if(displayMenu[m].parent_id == 0){
        _sh[0].items.push({
          id:displayMenu[m].id, title: t(displayMenu[m]['title']), path: _path, icon: icon(displayMenu[m].icon)
        })
      }else{
        for(var i=0; i<_sh[0].items.length;i++){
          if(_sh[0].items[i].id == displayMenu[m].parent_id){
            if('children' in _sh[0].items[i])
              _sh[0].items[i].children.push({id:displayMenu[m].id,title: t(displayMenu[m]['title']), path: _path})
            else
            _sh[0].items[i]['children'] = [{id:displayMenu[m].id,title: t(displayMenu[m]['title']), path: _path}];
          }
        }
      }
    }else{
      subheader.push({
        subheader: t(displayMenu[m]['subheader']),
        items: [
          { id:displayMenu[m].id,title: t(displayMenu[m]['title']), path: _path, icon: icon(displayMenu[m].icon) }
        ]
      })
    }
  }
  
  const data = useMemo(
    () => subheader,[t, user]
  );

  return data;
}
